import { Injectable } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { EmployeeItem } from '../private/terminal/repositories/employee.repository';
import { TerminalDataService } from '../private/terminal/services/terminal-data.service';
import { TerminalConfig } from '../models/terminal-config';
import { CompanyStorage } from '../_core/company-storage';
import { ApiV1Service } from './api-v1.service';

export class TerminalSession {
  id: number;
  uuid: string;
  full_name: string;
  employee_id: string;
  start: Date;
  end: Date;
  changed?: boolean;
  store_id: number;
  employee: EmployeeItem;

  constructor(employeeId: string, storeId: number) {
    this.id = 0;
    this.store_id = storeId;
    this.uuid = UUID.UUID();
    this.employee_id = employeeId;
    this.start = new Date();
    this.end = null;
    this.changed = true;
  }
}

@Injectable({ providedIn: 'root' })
export class TerminalAuthService {
  private _terminalSession: TerminalSession;
  get currentSession(): TerminalSession {
    return this._terminalSession;
  }

  get currentUser(): EmployeeItem {
    return this._terminalSession?.employee;
  }

  private _terminalConfig: TerminalConfig;
  get terminalConfig(): TerminalConfig {
    return this._terminalConfig;
  }

  constructor(
    private _terminalData: TerminalDataService,
    private _companyStorage: CompanyStorage,
    private _api: ApiV1Service
  ) {}

  async syncTerminalConfig() {
    this._terminalConfig = await this._api.getTerminalSettings2().toPromise();
    await this._companyStorage.set('TERMINAL_CONFIG', this._terminalConfig);
  }

  async loadTerminalConfig() {
    const config = await this._companyStorage.get('TERMINAL_CONFIG');
    if (config) {
      this._terminalConfig = config;
    } else {
      this.syncTerminalConfig();
    }

    return this._terminalConfig;
  }

  getCurrentUser() {
    return this._terminalSession?.employee;
  }

  getTerminalSession(): TerminalSession {
    if (!this._terminalSession) {
      return null;
    }

    return this._terminalSession;
  }

  startTerminalSession(session: TerminalSession) {
    this._terminalSession = session;
  }

  endTerminalSession() {
    this._terminalSession = null;
  }

  hasPermissions(permissions: any[]): boolean {
    const terminalData = this._terminalData.terminalSettings();

    const stringPermissions = <string[]>(
      permissions.filter((p): p is string => typeof p === 'string')
    );

    const booleanPermissions = permissions.filter<boolean>(
      (p): p is boolean => typeof p === 'boolean'
    );

    if (!terminalData || !terminalData.terminal?.useroles) {
      return booleanPermissions.every((p) => p === true);
    } else {
      if (!this._terminalSession || !this._terminalSession.employee) {
        return false;
      }

      const employeePermissions =
        this._terminalSession.employee.permissions || [];

      const permissionsFound = stringPermissions.filter((e) =>
        employeePermissions.includes(e)
      );
      return permissionsFound.length == stringPermissions.length;
    }
  }
}
